import { Button, Group, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { OpportunityPositionItem, PositionRequest } from '../../../models/opportunity.model';
import { useUpdatePosition } from '../hooks/usePositionMutations';
import { positionToFormRow } from '../utils/position.utils';
import { PositionEditor, PositionFormRow } from './PositionEditor';

interface PositionEditModalProps {
  position: OpportunityPositionItem;
  opportunityId: number;
  opened: boolean;
  onClose: () => void;
}

export const PositionEditModal = ({ position, opportunityId, opened, onClose }: PositionEditModalProps) => {
  const [formRow, setFormRow] = useState<PositionFormRow>(() => positionToFormRow(position));
  const updatePosition = useUpdatePosition(opportunityId);

  const handleSave = () => {
    const data: PositionRequest = {
      title: formRow.title.trim(),
      description: formRow.description.trim() || null,
      headCount: formRow.headCount,
      sortOrder: formRow.sortOrder,
      technologies: formRow.technologies.map((t) => ({
        technologyCanonicalId: t.technologyCanonicalId,
        requiredLevel: t.requiredLevel,
        requiredWeight: t.requiredWeight,
      })),
      certifications: formRow.certifications.map((c) => ({
        certificateId: c.certificateId ?? null,
        certificationFamilyId: c.certificationFamilyId ?? null,
      })),
      languages: formRow.languages.map((l) => ({
        languageId: l.languageId,
        requiredProficiencyId: l.requiredProficiencyId ?? null,
        requiredWeight: l.requiredWeight,
      })),
    };
    updatePosition.mutate(
      { positionId: position.id, data },
      {
        onSuccess: () => {
          notifications.show({ title: 'Saved', message: 'Position updated.', color: 'sopraGreen' });
          onClose();
        },
        onError: () => {
          notifications.show({ title: 'Error', message: 'Failed to save position.', color: 'red' });
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Edit position — {position.title}</Text>}
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        <PositionEditor
          position={formRow}
          index={0}
          onChange={setFormRow}
          onRemove={onClose}
          canRemove={false}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={updatePosition.isPending}>Cancel</Button>
          <Button variant="filled" color="brand.5" onClick={handleSave} loading={updatePosition.isPending}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
};
